import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import mixpanelService, { isAnalyticsExcludedEmail } from '../../services/mixpanelService';
import BrandLogo from '../BrandLogo';

const EmailConfirmation = ({ onLoginClick }) => {
  const [status, setStatus] = useState('confirming'); // 'confirming', 'success', 'error'
  const [message, setMessage] = useState('Confirming your email...');

  useEffect(() => {
    const confirmEmail = async () => {
      // Get the token and email from URL
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      const email = params.get('email'); // Get email if provided
      const type = params.get('type') || 'signup';

      if (isAnalyticsExcludedEmail(email)) {
        mixpanelService.setUserContext({ email });
      }
      
      if (!token) {
        setStatus('error');
        setMessage('Invalid confirmation link. Please check your email for the correct link.');
        mixpanelService.track('Email Confirmation Failed', { failure_mode: 'missing_token', type });
        return;
      }

      try {
        // Call backend to confirm the email
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/auth/confirm-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token, type, email }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setStatus('success');
          setMessage(data.message || 'Email confirmed successfully! You can now log in.');
          mixpanelService.track('Email Confirmation Completed', { type });
          
          // Redirect to login after 3 seconds
          setTimeout(() => {
            if (onLoginClick) {
              onLoginClick();
            } else {
              // If no callback, try to navigate to login
              window.location.href = '/login';
            }
          }, 3000);
        } else {
          setStatus('error');
          
          // Handle different error types
          const errorType = data.error_type || 'unknown';
          let errorMessage = data.message || 'Failed to confirm email.';
          
          if (errorType === 'expired') {
            errorMessage = 'This confirmation link has expired. Please sign up again to receive a new confirmation email.';
          } else if (errorType === 'already_verified') {
            errorMessage = 'Your email has already been verified. You can now log in with your credentials.';
            // Redirect to login after showing message
            setTimeout(() => {
              if (onLoginClick) {
                onLoginClick();
              } else {
                window.location.href = '/login';
              }
            }, 3000);
          } else if (errorType === 'invalid') {
            errorMessage = 'This confirmation link is invalid. Please check that you clicked the correct link from your email.';
          }
          
          setMessage(errorMessage);
          mixpanelService.track('Email Confirmation Failed', {
            type,
            status_code: response.status,
            failure_mode: errorType
          });
        }
      } catch (error) {
        console.error('Email confirmation error:', error);
        setStatus('error');
        setMessage('Unable to connect to the server. Please check your internet connection and try again.');
        mixpanelService.trackFailure('email_confirmation', error, { type });
      }
    };

    confirmEmail();
  }, [onLoginClick]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100">
      <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md">
        <BrandLogo className="mb-6" />
        <div className="text-center">
          {status === 'confirming' && (
            <>
              <div className="mx-auto w-16 h-16 mb-4">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirming Email</h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircleIcon className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Confirmed!</h2>
              <p className="text-gray-600 mb-4">{message}</p>
              <p className="text-sm text-gray-500">Redirecting to login...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <XCircleIcon className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirmation Failed</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <button
                onClick={onLoginClick || (() => window.location.href = '/login')}
                className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 transition duration-200"
              >
                Go to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailConfirmation;
